import Mark from "mark.js";
import type { MaybeRefOrGetter } from "vue";

export interface MarkOptions {
    element: string;
    className: string;
    exclude: string[];
    separateWordSearch: boolean;
    accuracy:
        | "partially"
        | "complementary"
        | "exactly"
        | {
              value: "partially" | "complementary" | "exactly";
              limiters: string[];
          };
    diacritics: boolean;
    synonyms: Record<string, string>;
    iframes: boolean;
    iframesTimeout: number;
    acrossElements: boolean;
    caseSensitive: boolean;
    ignoreJoiners: boolean;
    ignorePunctuation: string[];
    wildcards: "disabled" | "enabled" | "withSpaces";
    each: (mark: HTMLSpanElement) => void;
    filter: (
        match: string,
        term: string,
        total: number,
        termTotal: number,
    ) => boolean;
    noMatch: (term: string) => void;
    done: (total: number) => void;
    debug: boolean;
    log: object | typeof console;
}

export interface UseMarkOptions {
    element: MaybeRefOrGetter<HTMLElement>;
    markOptions: Partial<MarkOptions>;
}

export default function useMark(
    text: MaybeRefOrGetter<string>,
    keyword: MaybeRefOrGetter<string>,
    options: MaybeRefOrGetter<Partial<UseMarkOptions>> = {},
) {
    const textRef = toRef(text);
    const keywordRef = toRef(keyword);
    const optionsRef =
        toRef<MaybeRefOrGetter<Partial<UseMarkOptions>>>(options);
    const outputRef = toRef("");
    const mark = () => {
        const element = document.createElement("p");
        const instance = new Mark(element);
        element.innerHTML = textRef.value;
        instance.mark(keywordRef.value, optionsRef.value);
        outputRef.value = element.innerHTML;
    };

    watch([textRef, keywordRef, optionsRef], mark);

    if (process.client) {
        mark();
    } else {
        useNuxtApp().hook("page:loading:end", () => mark());
    }

    return computed(() => {
        return outputRef.value ? outputRef.value : textRef.value;
    });
}
